import { useAssistantRequest } from "../../apis/assistant/useAssistantRequest"
import { useConversationsRequests } from "../../apis/conversations/useConversationsRequests"
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest"
import { useAppHandlers } from "../app/useAppHandlers"

const MAX_CONVERSATIONS = 10
const MAX_MESSAGES_PER_CONV = 30

const HUMAN_DELAY_MIN_MS = 300
const HUMAN_DELAY_MAX_MS = 1000

/**
 * Random pause between sequential LinkedIn extension calls so the traffic pattern
 * resembles a human browsing the inbox rather than a tight scripted loop.
 */
const humanDelay = (minMs = HUMAN_DELAY_MIN_MS, maxMs = HUMAN_DELAY_MAX_MS): Promise<void> => {
    const ms = Math.floor(Math.random() * (Math.max(maxMs, minMs) - minMs + 1)) + minMs
    return new Promise(resolve => setTimeout(resolve, ms))
}

export const useAssistantHandler = () => {
    const { 
        generateMessage, 
        sendChatMessage,
        streamChatMessage,
        getUserConversations,
        getConversation,
        archiveConversation,
        deleteConversation,
        cancelChat,
        saveConversationArchive,
        composeInboxNextMessage,
        tagInboxConversation
    } = useAssistantRequest()

    const { getConversationsPage, getMessagesFromConversation } = useExtensionRequest()
    const { getInboxQueryParams } = useConversationsRequests()
    const { showSuccessToast, showErrorToast, showInfoToast } = useAppHandlers()
    

    const generateMessageHandler = async (payload: any) => {

        const result = await generateMessage(payload)

        if(result) return result.newMesage
    }

    const sendChatMessageHandler = async (message: string, conversationId?: string) => {
        const result = await sendChatMessage({ message, conversationId })
        return result
    }

    const streamChatMessageHandler = (params: { 
        message: string; 
        conversationId?: string; 
        onEvent?: (event: { type: string; data?: any }) => void;
    }) => {
        return streamChatMessage(params)
    }

    const getUserConversationsHandler = async (params?: { limit?: number, skip?: number }) => {
        const result = await getUserConversations(params)
        return result?.conversations || []
    }

    const getConversationHandler = async (conversationId: string) => {
        const result = await getConversation(conversationId)
        return result?.conversation
    }

    const archiveConversationHandler = async (conversationId: string) => {
        await archiveConversation(conversationId)
    }

    const deleteConversationHandler = async (conversationId: string) => {
        await deleteConversation(conversationId)
    }

    const cancelChatHandler = () => {
        cancelChat()
    }

    const trainAiByConversationsHandler = async (): Promise<boolean> => {
        console.log('[TrainAI] ── START ─────────────────────────────────────────────────')
        console.log('[TrainAI] Step 1: fetch conversations from LinkedIn via extension')
        console.log('[TrainAI] Step 2: fetch messages per conversation via extension')
        console.log('[TrainAI] Step 3: POST everything to backend ONLY after all data is ready')

        // ── Step 1: fetch conversations directly from LinkedIn via extension (V1 GraphQL) ──
        let voyagerMessagingGraphQL: any = undefined
        try {
            const qp: any = await getInboxQueryParams()
            voyagerMessagingGraphQL = qp?.data ?? qp
        } catch (e) {
            console.warn('[TrainAI] /inbox/queryParams pao:', e)
        }

        const allConversations: any[] = []
        let nextCursor: string | undefined = ''
        let keepPaging = true

        while (keepPaging && allConversations.length < MAX_CONVERSATIONS) {
            console.log(`[TrainAI] getConversationsPage – nextCursor: ${nextCursor || 'initial'}`)
            const rawConvs: any = await getConversationsPage(undefined, voyagerMessagingGraphQL ? { voyagerMessagingGraphQL, nextCursor } : undefined)
            console.log('[TrainAI] getConversationsPage raw result:', rawConvs)

            // V1 vraća { conversations, nextCursor }; podržavamo i stari oblik (niz / elements)
            let page: any[] = []
            if (Array.isArray(rawConvs))                       page = rawConvs
            else if (Array.isArray(rawConvs?.conversations))   page = rawConvs.conversations
            else if (Array.isArray(rawConvs?.elements))        page = rawConvs.elements

            const newCursor: string | undefined = rawConvs?.nextCursor?.nextCursor ?? rawConvs?.nextCursor

            console.log(`[TrainAI] conversations on this page: ${page.length}`, { nextCursor: newCursor })
            if (page.length > 0) console.log('[TrainAI] first conv sample:', page[0])

            if (page.length === 0) { keepPaging = false; break }

            for (const conv of page) {
                allConversations.push(conv)
                if (allConversations.length >= MAX_CONVERSATIONS) break
            }

            // V1: koristimo nextCursor; stop ako više nema cursor-a ili je isti.
            if (!newCursor || newCursor === nextCursor) {
                keepPaging = false
            } else {
                nextCursor = newCursor
                // Human-like pause between conversation-batch fetches.
                const t0 = Date.now()
                await humanDelay()
                console.log(`[TrainAI] human-pause after conversations batch: ${Date.now() - t0}ms`)
            }
        }

        console.log(`[TrainAI] Total conversations fetched from LinkedIn: ${allConversations.length}`)

        if (allConversations.length === 0) {
            console.warn('[TrainAI] No conversations returned – check extension logs above (Response from extension:)')
            showInfoToast({ message: 'No conversations found. Make sure you are logged into LinkedIn.' })
            return false
        }

        // ── Step 2: fetch messages for every conversation ────────────────────
        // Nothing is sent to backend until ALL messages are fetched.
        const batch: any[] = []

        for (let i = 0; i < allConversations.length; i++) {
            const conv = allConversations[i]
            // conversationId field may differ by extension version
            const convId: string = conv.conversationId ?? conv.entityUrn ?? conv.id ?? ''

            console.log(`\n[TrainAI] [${i + 1}/${allConversations.length}] convId: ${convId}`)
            console.log('[TrainAI] conv keys:', Object.keys(conv))
            console.log('[TrainAI] participants:', conv.participants)

            if (!convId) {
                console.warn('[TrainAI] Skipping – no id field found on conv:', conv)
                continue
            }

            // Page 1 of messages (newest ≤20)
            console.log(`[TrainAI] getMessagesFromConversation page 1 for: ${convId}`)
            const rawMsgs1: any = await getMessagesFromConversation(convId)
            console.log(`[TrainAI] raw messages (page 1):`, rawMsgs1)

            let msgs: any[] = Array.isArray(rawMsgs1) ? rawMsgs1 : []
            console.log(`[TrainAI] messages count (page 1): ${msgs.length}`)
            if (msgs.length > 0) console.log('[TrainAI] first message sample:', msgs[0])

            // If a full page (20) came back try one more page → up to 30 total
            if (msgs.length >= 20) {
                const oldestTs = msgs[msgs.length - 1]?.dateTimestamp
                // Human-like pause between page 1 and page 2 ("scrolling up to load older messages").
                const t0 = Date.now()
                await humanDelay()
                console.log(`[TrainAI] human-pause before messages page 2: ${Date.now() - t0}ms`)
                console.log(`[TrainAI] getMessagesFromConversation page 2 – cursor: ${oldestTs}`)
                const rawMsgs2: any = await getMessagesFromConversation(convId, oldestTs)
                const msgs2: any[] = Array.isArray(rawMsgs2) ? rawMsgs2 : []
                console.log(`[TrainAI] messages count (page 2): ${msgs2.length}`)
                msgs = [...msgs, ...msgs2.slice(0, MAX_MESSAGES_PER_CONV - msgs.length)]
            }

            const formattedMessages = msgs
                .slice(0, MAX_MESSAGES_PER_CONV)
                .map((m: any) => ({
                    sender: m.isSent ? 'user' : 'peer',
                    text: typeof m.text === 'string' ? m.text.trim() : '',
                    ...(m.dateTimestamp ? { sentAt: new Date(m.dateTimestamp).toISOString() } : {})
                }))
                .filter((m: any) => m.text.length > 0)

            console.log(`[TrainAI] formatted messages: ${formattedMessages.length}`)

            if (formattedMessages.length === 0) {
                console.warn('[TrainAI] Skipping conv – no valid messages after formatting')
                continue
            }

            const firstParticipant = conv.participants?.length === 1 ? conv.participants[0] : null
            const peer = firstParticipant ? {
                displayName: `${firstParticipant.firstName || ''} ${firstParticipant.lastName || ''}`.trim() || undefined,
                profileId:         firstParticipant.profileId         || undefined,
                publicIdentifier:  firstParticipant.publicIdentifier  || undefined
            } : undefined

            console.log(`[TrainAI] peer:`, peer)

            batch.push({
                threadKey: `linkedin_${convId}`,
                messages:  formattedMessages,
                ...(peer ? { peer } : {}),
                source: 'linkedin_inbox'
            })
            console.log(`[TrainAI] ✓ batch size: ${batch.length}`)
        }

        console.log('\n[TrainAI] ── BATCH READY ───────────────────────────────────────────')
        console.log(`[TrainAI] Conversations with messages: ${batch.length} / ${allConversations.length}`)
        batch.forEach((b, i) =>
            console.log(`  [${i + 1}] ${b.threadKey}  messages: ${b.messages.length}`)
        )

        if (batch.length === 0) {
            console.warn('[TrainAI] Nothing to send')
            showInfoToast({ message: 'No conversations with messages found.' })
            return false
        }

        // ── Step 3: POST everything to backend ───────────────────────────────
        console.log('[TrainAI] POSTing to /ai-assistant/conversation-archive...')
        const result: any = await saveConversationArchive({ conversations: batch, source: 'linkedin_inbox' })
        console.log('[TrainAI] Backend response:', result)

        if (result) {
            showSuccessToast({ message: `AI trained with ${result.recorded ?? batch.length} conversation(s).` })
            return true
        }

        console.warn('[TrainAI] Backend returned falsy – check network tab')
        showErrorToast({ message: 'Failed to save conversations for AI training.' })
        return false
    }

    /**
     * Asks backend (POST /ai-assistant/inbox/compose-next) to draft the next outbound
     * inbox message for the user, given the current LinkedIn conversation context.
     *
     * Sends `linkedin_<conversationId>` as the threadKey so the backend can exclude the
     * current thread from the lexical-overlap retrieval against UserConversationArchive –
     * mirrors the format used in `trainAiByConversationsHandler`.
     */
    const composeInboxNextMessageHandler = async (params: {
        conversationId: string
        messages: any[]
        topSimilar?: number
        scanLimit?: number
    }): Promise<string> => {
        const { conversationId, messages, topSimilar, scanLimit } = params
        if (!conversationId || !Array.isArray(messages)) return ''

        const formattedMessages = messages
            .map((m: any) => ({
                sender: (m?.isSent ? 'user' : 'peer') as 'user' | 'peer',
                text: typeof m?.text === 'string' ? m.text.trim() : '',
                ...(m?.dateTimestamp ? { sentAt: new Date(m.dateTimestamp).toISOString() } : {})
            }))
            .filter((m: any) => m.text.length > 0)
            .slice(-MAX_MESSAGES_PER_CONV)

        if (!formattedMessages.length) {
            showInfoToast({ message: 'No messages in this conversation to draft a reply from.' })
            return ''
        }

        const result: any = await composeInboxNextMessage({
            conversationId: `linkedin_${conversationId}`,
            messages: formattedMessages,
            ...(topSimilar != null ? { topSimilar } : {}),
            ...(scanLimit != null ? { scanLimit } : {})
        })

        const suggestion: string = (result?.suggestedMessage || '').toString().trim()
        if (!suggestion) {
            showErrorToast({ message: 'AI could not draft a reply. Please try again.' })
            return ''
        }
        return suggestion
    }

    const tagInboxConversationHandler = async (params: {
        conversationId: string
        messages: any[]
    }): Promise<{ conversationTags: Array<{ tag: string; group?: string }>; reason: string } | null> => {
        const { conversationId, messages } = params
        if (!conversationId || !Array.isArray(messages)) return null

        const formattedMessages = messages
            .map((m: any) => ({
                sender: (m?.isSent ? 'user' : 'peer') as 'user' | 'peer',
                text: typeof m?.text === 'string' ? m.text.trim() : '',
                ...(m?.dateTimestamp ? { sentAt: new Date(m.dateTimestamp).toISOString() } : {})
            }))
            .filter((m: any) => m.text.length > 0)
            .slice(-MAX_MESSAGES_PER_CONV)

        if (!formattedMessages.length) {
            showInfoToast({ message: 'No messages in this conversation to classify.' })
            return null
        }

        const result: any = await tagInboxConversation({
            conversationId: `linkedin_${conversationId}`,
            messages: formattedMessages,
        })

        const tags = result?.conversationTags ?? result?.labels?.map((l: { label: string; group?: string }) => ({
            tag: l.label,
            group: l.group,
        }))
        if (!tags?.length) {
            showErrorToast({ message: 'AI could not classify this conversation. Please try again.' })
            return null
        }

        return { conversationTags: tags, reason: result.reason }
    }

    return {
        generateMessageHandler,
        sendChatMessageHandler,
        streamChatMessageHandler,
        getUserConversationsHandler,
        getConversationHandler,
        archiveConversationHandler,
        deleteConversationHandler,
        cancelChatHandler,
        trainAiByConversationsHandler,
        composeInboxNextMessageHandler,
        tagInboxConversationHandler
    }
}
